// webapp/src/lib/ipscScoring.js
//
// IPSC Comstock scoring engine — pure functions, no stores, no side effects.
// Twin of backend/lib/ipsc-scoring.js (stageDefFromStand / stageMaxPoints /
// validateSheet / scoreSheet / round4 must stay line-for-line equal; both are
// asserted against webapp/tests/fixtures/ipsc/scoresheets.json).
//
// Vocabulary: `stand` = one IPSC stage (convention.stageStands[] entry),
// `division` = participant.class, a "sheet" = one shooter × one stage.

import {
    DIVISIONS, hitValues, isValidDivision, NS_MAX_PER_TARGET, DEFAULT_HITS_PER_PAPER,
} from './ipsc';

const num = v => Number(v) || 0;

export function round4(n) {
    return Math.round((Number(n) || 0) * 10000) / 10000;
}

export function stageDefFromStand(stand = {}) {
    const paper = num(stand.ipscPaperTargets);
    const hitsPerPaper = num(stand.ipscHitsPerPaper) || DEFAULT_HITS_PER_PAPER;
    const poppers = num(stand.ipscPoppers);
    const plates = num(stand.ipscPlates);
    const noShoots = num(stand.ipscNoShoots);
    const minRounds = num(stand.ipscMinRounds) || (paper * hitsPerPaper + poppers + plates);
    return {
        stand: num(stand.stand),
        paper,
        hitsPerPaper,
        poppers,
        plates,
        noShoots,
        scoringMethod: stand.ipscScoringMethod || 'comstock',
        minRounds,
    };
}

export function stageMaxPoints(stageDef = {}) {
    const def = stageDef;
    return num(def.paper) * (num(def.hitsPerPaper) || DEFAULT_HITS_PER_PAPER) * 5 + (num(def.poppers) + num(def.plates)) * 5;
}

const COUNT_FIELDS = ['hitsA', 'hitsC', 'hitsD', 'misses', 'steelHits', 'noShoots', 'procedurals'];

export function validateSheet(sheet = {}, stageDef = {}) {
    const errors = [];
    if (COUNT_FIELDS.some(f => num(sheet[f]) < 0) || num(sheet.time) < 0) {
        errors.push('negativeCount');
    }
    // A DNF / DQ sheet carries whatever the RO had time to note; counts are not
    // required to add up (the stage scores zero either way).
    if (sheet.dnf || sheet.dq) {
        return errors;
    }
    const paperRequired = num(stageDef.paper) * (num(stageDef.hitsPerPaper) || DEFAULT_HITS_PER_PAPER);
    const paperHits = num(sheet.hitsA) + num(sheet.hitsC) + num(sheet.hitsD) + num(sheet.misses);
    if (paperHits !== paperRequired) {
        errors.push('paperHitsMismatch');
    }
    if (num(sheet.steelHits) > num(stageDef.poppers) + num(stageDef.plates)) {
        errors.push('steelHitsOverCount');
    }
    if (num(sheet.noShoots) > num(stageDef.noShoots) * NS_MAX_PER_TARGET) {
        errors.push('noShootsOverCap');
    }
    if (!(num(sheet.time) > 0)) {
        errors.push('timeRequired');
    }
    return errors;
}

export function scoreSheet(sheet = {}, stageDef = {}, powerFactor = 'minor') {
    const errors = validateSheet(sheet, stageDef);
    const v = hitValues(powerFactor);
    const steelCount = num(stageDef.poppers) + num(stageDef.plates);
    const steelMisses = Math.max(0, steelCount - num(sheet.steelHits));
    const raw = num(sheet.hitsA) * v.A
        + num(sheet.hitsC) * v.C
        + num(sheet.hitsD) * v.D
        + num(sheet.steelHits) * v.steel
        + (num(sheet.misses) + steelMisses) * v.miss
        + num(sheet.noShoots) * v.noShoot
        + num(sheet.procedurals) * v.procedural;
    const zeroed = !!(sheet.dnf || sheet.dq);
    const stageScore = zeroed ? 0 : Math.max(0, raw);
    const time = num(sheet.time);
    const hitFactor = (!zeroed && time > 0) ? round4(stageScore / time) : 0;
    return {
        stageScore, hitFactor, steelMisses, valid: errors.length === 0, errors,
    };
}

// Dense ranking: descending by `scoreKey`, equal values share a place, the next
// distinct value gets place + 1. Stable by `name` so lists do not jump around.
export function rankDense(items, scoreKey) {
    const sorted = [...items].sort((a, b) => (num(b[scoreKey]) - num(a[scoreKey]))
        || String(a.name || '').localeCompare(String(b.name || ''), 'nb'));
    let place = 0;
    let prev = null;
    return sorted.map((item) => {
        if (prev === null || num(prev[scoreKey]) !== num(item[scoreKey])) {
            place += 1;
        }
        prev = item;
        return { ...item, place };
    });
}

function shooterName(entry) {
    return `${entry.firstname || ''} ${entry.lastname || ''}`.trim();
}

function pickCounts(row) {
    return {
        A: num(row.hitsA),
        C: num(row.hitsC),
        D: num(row.hitsD),
        M: num(row.misses),
        steelHits: num(row.steelHits),
        noShoots: num(row.noShoots),
        procedurals: num(row.procedurals),
    };
}

// Append DQ shooters after the ranked list, all sharing the next place.
function rankWithDqLast(list, scoreKey) {
    const ranked = rankDense(list.filter(s => !s.dq), scoreKey);
    const lastPlace = ranked.length ? ranked[ranked.length - 1].place : 0;
    const dqs = list.filter(s => s.dq).map(s => ({ ...s, place: lastPlace + 1 }));
    return [...ranked, ...dqs];
}

/**
 * Build the whole result picture for one IPSC convention.
 *
 * @param {object} convention   needs stageStands[]
 * @param {object[]} scores     conventionIpscScore rows (stored stageScore/hitFactor are ignored —
 *                              everything is recomputed from the counts + the current stage defs)
 * @param {object[]} squadEntries flattened participants1..N entries
 *                              { id, firstname, lastname, class, powerFactor, ipscCategory, sportsClub, resultStatus }
 */
export function buildMatchResults({ convention = {}, scores = [], squadEntries = [] } = {}) {
    const stageDefs = {};
    (convention.stageStands || []).forEach((stand) => {
        const def = stageDefFromStand(stand);
        if (def.stand > 0) {
            stageDefs[def.stand] = def;
        }
    });
    const stands = Object.keys(stageDefs).map(Number).sort((a, b) => a - b);

    // One shooter entry per (user, division).
    const shooters = {};
    const keyOf = (participant, division) => `${participant}::${division}`;
    squadEntries.forEach((entry) => {
        const division = entry.class;
        if (!entry || !entry.id || !isValidDivision(division)) {
            return;
        }
        const key = keyOf(entry.id, division);
        if (!shooters[key]) {
            shooters[key] = {
                participant: num(entry.id),
                division,
                powerFactor: entry.powerFactor === 'major' ? 'major' : 'minor',
                name: shooterName(entry),
                club: entry.sportsClub || '',
                category: entry.ipscCategory || '',
                dq: entry.resultStatus === 'dsq',
                dqStand: null,
                dnfStands: [],
                rows: {},
                stagePoints: {},
                stageHF: {},
                matchPoints: 0,
                matchPercent: 0,
            };
        }
    });
    scores.forEach((row) => {
        if (!row || row.offline > 0 || !isValidDivision(row.division)) {
            return;
        }
        const key = keyOf(row.participant, row.division);
        if (!shooters[key]) {
            // Orphan sheet (shooter moved division after scoring) — still listed, see spec.
            shooters[key] = {
                participant: num(row.participant),
                division: row.division,
                powerFactor: row.powerFactor === 'major' ? 'major' : 'minor',
                name: row.scoredFor || `#${row.participant}`,
                club: '',
                category: '',
                dq: false,
                dqStand: null,
                dnfStands: [],
                rows: {},
                stagePoints: {},
                stageHF: {},
                matchPoints: 0,
                matchPercent: 0,
            };
        }
        const s = shooters[key];
        const stand = num(row.stand);
        s.rows[stand] = row;
        if (row.dq) {
            s.dq = true;
            s.dqStand = s.dqStand === null ? stand : Math.min(s.dqStand, stand);
        }
        if (row.dnf && !s.dnfStands.includes(stand)) {
            s.dnfStands.push(stand);
        }
    });

    const divisions = {};
    DIVISIONS.forEach((d) => {
        const list = Object.values(shooters).filter(s => s.division === d.key);
        if (list.length === 0) {
            return;
        }
        const stages = {};
        stands.forEach((stand) => {
            const def = stageDefs[stand];
            const max = stageMaxPoints(def);
            const computed = list.map((s) => {
                const row = s.rows[stand];
                if (!row) {
                    return null;
                }
                const pf = row.powerFactor === 'major' || row.powerFactor === 'minor' ? row.powerFactor : s.powerFactor;
                const result = scoreSheet(row, def, pf);
                // Validity is an ENTRY-time gate (the RO page / server refuse invalid
                // sheets). A stage definition edited after scoring must not zero the
                // sheets already on file: they are scored from their counts and flagged.
                const eligible = !s.dq && !row.dnf && !row.dq;
                return {
                    shooter: s, row, result, eligible,
                };
            }).filter(Boolean);
            const bestHF = computed.reduce((m, c) => (c.eligible && c.result.hitFactor > m ? c.result.hitFactor : m), 0);
            const stageRows = computed.map((c) => {
                const hf = c.eligible ? c.result.hitFactor : 0;
                const points = (c.eligible && bestHF > 0) ? round4((hf / bestHF) * max) : 0;
                const percent = (c.eligible && bestHF > 0) ? round4((hf / bestHF) * 100) : 0;
                c.shooter.stagePoints[stand] = points;
                c.shooter.stageHF[stand] = hf;
                return {
                    participant: c.shooter.participant,
                    name: c.shooter.name,
                    hitFactor: hf,
                    time: num(c.row.time),
                    counts: pickCounts(c.row),
                    stagePoints: points,
                    stagePercent: percent,
                    dnf: !!c.row.dnf,
                    dq: !!c.shooter.dq,
                    invalid: !c.result.valid,
                };
            });
            const shot = stageRows.filter(r => !r.dnf && !r.dq);
            const notShot = stageRows.filter(r => r.dnf || r.dq);
            const ranked = rankDense(shot, 'stagePoints');
            const lastPlace = ranked.length ? ranked[ranked.length - 1].place : 0;
            stages[stand] = [...ranked, ...notShot.map(r => ({ ...r, place: lastPlace + 1 }))];
        });

        list.forEach((s) => {
            stands.forEach((stand) => {
                if (s.stagePoints[stand] == null) {
                    s.stagePoints[stand] = 0;
                    s.stageHF[stand] = 0;
                }
            });
            s.matchPoints = s.dq ? 0 : round4(stands.reduce((sum, stand) => sum + s.stagePoints[stand], 0));
        });
        const bestMatch = list.reduce((m, s) => (!s.dq && s.matchPoints > m ? s.matchPoints : m), 0);
        const withPercent = list.map((s) => {
            const { rows, ...rest } = s;
            return {
                ...rest,
                matchPercent: (!s.dq && bestMatch > 0) ? round4((s.matchPoints / bestMatch) * 100) : 0,
            };
        });
        const rankedShooters = rankWithDqLast(withPercent, 'matchPoints');

        const categories = {};
        rankedShooters.forEach((s) => {
            if (!s.category) {
                return;
            }
            if (!categories[s.category]) {
                categories[s.category] = [];
            }
            categories[s.category].push(s);
        });
        Object.keys(categories).forEach((cat) => {
            const members = categories[cat];
            const best = members.reduce((m, s) => (!s.dq && s.matchPoints > m ? s.matchPoints : m), 0);
            categories[cat] = rankWithDqLast(members.map(s => ({
                ...s,
                matchPercent: (!s.dq && best > 0) ? round4((s.matchPoints / best) * 100) : 0,
            })), 'matchPoints');
        });

        divisions[d.key] = {
            key: d.key,
            label: d.label,
            shooters: rankedShooters,
            categories,
            stages,
        };
    });

    return { divisions, stageDefs };
}
